import { ApiError, request, type RoomState, type Session } from "./api";
type Slot = "audio" | "camera" | "screen";
export interface Remote {
	id: string;
	audio: MediaStream;
	camera: MediaStream;
	screen: MediaStream;
}
interface Connection {
	pc: RTCPeerConnection;
	remote: Remote;
	makingOffer: boolean;
	ignoreOffer: boolean;
	candidates: RTCIceCandidateInit[];
	initialized: boolean;
}
export class MeetingMesh {
	readonly peerId = crypto.randomUUID();
	state: RoomState | null = null;
	onState = (state: RoomState) => {};
	onRemote = (remotes: Remote[]) => {};
	onWarning = (message: string) => {};
	onEnded = () => {};
	private peers = new Map<string, Connection>();
	private tracks: Partial<Record<Slot, MediaStreamTrack>> = {};
	private timer: ReturnType<typeof setTimeout> | undefined;
	private stopped = false;
	private cursor = 0;
	private queue: Promise<unknown> = Promise.resolve();
	private ice: RTCIceServer[] = [];
	constructor(
		readonly id: string,
		readonly session: Session,
		readonly name: string,
		readonly recorder = false,
		private transport?: (body: Record<string, unknown>) => Promise<RoomState>,
	) {}
	async start(audio?: MediaStreamTrack) {
		if (audio) this.tracks.audio = audio;
		await this.command({
			type: "join",
			name: this.name,
			recorder: this.recorder,
		});
		this.schedule();
	}
	command(command: Record<string, unknown>): Promise<RoomState> {
		const next = this.queue.then(async () => {
			if (this.stopped) throw new Error("Meeting connection closed");
			const body = { ...command, peerId: this.peerId, after: this.cursor };
			const state = this.transport
				? await this.transport(body)
				: await request<RoomState>(
						`/api/v1/meetings/${this.id}`,
						this.session,
						body,
					);
			await this.accept(state);
			return state;
		});
		this.queue = next.catch(() => {});
		return next;
	}
	private schedule() {
		if (this.stopped) return;
		this.timer = setTimeout(async () => {
			try {
				await this.command({ type: "poll" });
				this.onWarning("");
			} catch (e) {
				if (e instanceof ApiError && e.status === 410) {
					this.onEnded();
					this.close();
					return;
				}
				this.onWarning(
					e instanceof ApiError && e.status === 401
						? "Wallet session expired. Existing calls continue; sign in again to reconnect."
						: "Meeting coordination reconnecting. Existing calls continue.",
				);
				if (e instanceof ApiError && e.status === 403) {
					try {
						await this.command({
							type: "join",
							name: this.name,
							recorder: this.recorder,
						});
					} catch {}
				}
			}
			this.schedule();
		}, 1000);
	}
	private async accept(state: RoomState) {
		this.state = state;
		if (state.iceServers) this.ice = state.iceServers;
		if (state.endedAt) {
			this.onState(state);
			this.onEnded();
			this.close();
			return;
		}
		for (const p of state.peers)
			if (p.id !== this.peerId && !this.peers.has(p.id)) this.connect(p.id);
		for (const [id, c] of this.peers)
			if (!state.peers.some((p) => p.id === id)) {
				c.pc.close();
				this.peers.delete(id);
			}
		for (const signal of state.signals) {
			const c = this.peers.get(signal.from);
			if (!c) continue;
			try {
				const { description, candidate } = signal.data;
				if (description) {
					const collision =
						description.type === "offer" &&
						(c.makingOffer || c.pc.signalingState !== "stable");
					c.ignoreOffer = this.peerId < signal.from && collision;
					if (c.ignoreOffer) continue;
					await c.pc.setRemoteDescription(description);
					if (!c.initialized) {
						for (const [index, slot] of (
							["audio", "camera", "screen"] as Slot[]
						).entries()) {
							const transceiver = c.pc.getTransceivers()[index];
							transceiver.direction = this.recorder ? "recvonly" : "sendrecv";
							await transceiver.sender.replaceTrack(this.tracks[slot] ?? null);
						}
						c.initialized = true;
					}
					for (const pending of c.candidates.splice(0))
						await c.pc.addIceCandidate(pending);
					if (description.type === "offer") {
						await c.pc.setLocalDescription();
						this.sendSignal(signal.from, {
							description: c.pc.localDescription?.toJSON(),
						});
					}
				} else if (candidate && !c.ignoreOffer) {
					if (c.pc.remoteDescription) await c.pc.addIceCandidate(candidate);
					else c.candidates.push(candidate);
				}
			} catch {
				this.onWarning("A peer connection is recovering.");
			}
		}
		this.cursor = Math.max(this.cursor, state.cursor);
		this.onState(state);
		this.emitRemotes();
	}
	private sendSignal(to: string, data: unknown) {
		void this.command({ type: "signal", to, data }).catch(() =>
			this.onWarning("Reconnecting to a participant…"),
		);
	}
	private connect(id: string) {
		const pc = new RTCPeerConnection({ iceServers: this.ice });
		const remote = {
			id,
			audio: new MediaStream(),
			camera: new MediaStream(),
			screen: new MediaStream(),
		};
		const c: Connection = {
			pc,
			remote,
			makingOffer: false,
			ignoreOffer: false,
			candidates: [],
			initialized: false,
		};
		this.peers.set(id, c);
		// Only the initial offerer creates m-lines. The answerer adopts them,
		// otherwise glare creates six transceivers and scrambles audio/video slots.
		if (this.peerId < id)
			for (const slot of ["audio", "camera", "screen"] as Slot[]) {
				const t = pc.addTransceiver(slot === "audio" ? "audio" : "video", {
					direction: this.recorder ? "recvonly" : "sendrecv",
				});
				if (this.tracks[slot]) void t.sender.replaceTrack(this.tracks[slot]!);
			}
		c.initialized = this.peerId < id;
		pc.onicecandidate = ({ candidate }) => {
			if (candidate) this.sendSignal(id, { candidate: candidate.toJSON() });
		};
		pc.ontrack = ({ track, transceiver }) => {
			const index = pc.getTransceivers().indexOf(transceiver);
			const slot: Slot =
				index === 0 ? "audio" : index === 1 ? "camera" : "screen";
			remote[slot].getTracks().forEach((t) => remote[slot].removeTrack(t));
			remote[slot].addTrack(track);
			this.emitRemotes();
			track.onunmute = () => this.emitRemotes();
		};
		pc.onnegotiationneeded = async () => {
			if (!c.initialized) return;
			try {
				c.makingOffer = true;
				await pc.setLocalDescription();
				this.sendSignal(id, { description: pc.localDescription?.toJSON() });
			} catch {
				this.onWarning("Connecting media…");
			} finally {
				c.makingOffer = false;
			}
		};
		pc.onconnectionstatechange = () => {
			if (pc.connectionState === "failed") {
				this.onWarning("Recovering a peer connection…");
				pc.restartIce();
			}
		};
	}
	private emitRemotes() {
		this.onRemote([...this.peers.values()].map((p) => p.remote));
	}
	async setTrack(slot: Slot, track: MediaStreamTrack | null) {
		const index = { audio: 0, camera: 1, screen: 2 }[slot];
		await Promise.all(
			[...this.peers.values()].map((c) =>
				c.pc.getTransceivers()[index]?.sender.replaceTrack(track),
			),
		);
		const old = this.tracks[slot];
		if (old && old !== track) old.stop();
		if (track) this.tracks[slot] = track;
		else delete this.tracks[slot];
	}
	async leave() {
		try {
			await this.command({ type: "leave" });
		} finally {
			this.close();
		}
	}
	close() {
		this.stopped = true;
		clearTimeout(this.timer);
		for (const c of this.peers.values()) c.pc.close();
		this.peers.clear();
		Object.values(this.tracks).forEach((t) => t.stop());
	}
}
